import { v4 as uuidv4 } from "uuid";
/**
 * Checks if the given model string represents a custom object field, business field, or opportunity field.
 * @param {string | undefined} model - The model string to check.
 * @returns {boolean} - Returns true if the model is a custom object, business, or opportunity field, false otherwise.
 */
export const isCustomObjectField = (model?: string): boolean => {
  // Check if the model is not null or undefined
  if (!model) {
    return false;
  }

  // Check for custom objects, business, or opportunity models
  const parts = model.split(".");
  if (parts[0] === "custom_objects" || model === "business" || model === "opportunity") {
    return true;
  }

  return false;
};

/**
 * Check if a model represents Business or Opportunity.
 * These are allowed to coexist with other fields and should be ignored for mixed-object validation.
 * @param model - The model string to check
 * @returns True if the model is business or opportunity
 */
export const isBusinessOrOpportunity = (model?: string): boolean => {
  return model === "business" || model === "opportunity";
};

/**
 * Check if the given model strictly represents a Custom Object (excluding business/opportunity).
 * @param model - The model string to check
 * @returns True if the model is a real custom object
 */
export const isTrueCustomObjectField = (model?: string): boolean => {
  if (!model) {
    return false;
  }
  return model.split(".")[0] === "custom_objects";
};

/**
 * Get custom object name for display (singular)
 * @param model - The custom object model
 * @param customObjects - Array of custom objects from store
 * @param fallback - Optional fallback text (defaults to "Custom Object")
 * @returns Singular display name
 */
export const getCustomObjectName = (
  model: string,
  customObjects: any[],
  fallback = "Custom Object"
): string => {
  if (!model) {
    return fallback;
  }

  // Handle business and opportunity models
  if (model === "business") {
    return "Company";
  }
  if (model === "opportunity") {
    return "Opportunity";
  }

  // Handle custom objects
  if (!isCustomObjectField(model)) {
    return fallback;
  }

  // Find the custom object in store to get proper label
  const customObject = customObjects?.find((obj) => obj.key === model);
  return customObject?.labels?.singular || fallback;
};

/**
 * Get custom object name for display (plural)
 * @param model - The custom object model
 * @param customObjects - Array of custom objects from store
 * @param fallback - Optional fallback text (defaults to "Custom Objects")
 * @returns Plural display name
 */
export const getCustomObjectNamePlural = (
  model: string,
  customObjects: any[],
  fallback = "Custom Objects"
): string => {
  if (!model) {
    return fallback;
  }

  // Handle business and opportunity models
  if (model === "business") {
    return "Company Fields";
  }
  if (model === "opportunity") {
    return "Opportunity Fields";
  }

  // Handle custom objects
  if (!isCustomObjectField(model)) {
    return fallback;
  }

  // Find the custom object in store to get proper label
  const customObject = customObjects?.find((obj) => obj.key === model);
  return customObject?.labels?.plural || fallback;
};

/**
 * Check if a field is a primary field for a custom object, business, or opportunity
 * @param element - The element to check
 * @param customObjects - Array of custom objects from store
 * @returns Object with primary field information
 */
export const isPrimaryField = (
  element: any,
  customObjects: any[]
): { isPrimary: boolean; objectName: string; objectSingular: string; fieldName: string } => {
  if (!isCustomObjectField(element?.model)) {
    return { isPrimary: false, objectName: "", objectSingular: "", fieldName: "" };
  }

  // Handle business and opportunity models (they don't have custom primary fields)
  if (element.model === "business") {
    // Primary check: directly treat business.name as the primary key
    const fieldKey = (element.fieldKey || "").toLowerCase();
    const isBusinessPrimary = fieldKey === "business.name";

    return {
      isPrimary: isBusinessPrimary,
      objectName: "Company Fields",
      objectSingular: "Company Field",
      fieldName: element.label || element.name || "Field",
    };
  }
  if (element.model === "opportunity") {
    return {
      isPrimary: false,
      objectName: "Opportunity Fields",
      objectSingular: "Opportunity Field",
      fieldName: element.label || element.name || "Field",
    };
  }

  // Find the custom object in store
  const customObject = customObjects?.find((obj) => obj.key === element.model);
  if (!customObject) {
    return { isPrimary: false, objectName: "", objectSingular: "", fieldName: "" };
  }

  // Check if this field is the primary display property.
  // Support both full path and normalized key.suffix variants found in persisted builders.
  const fieldKey = (element.fieldKey || "").toString();
  const primary = (customObject.primaryDisplayProperty || "").toString();
  const primarySuffix = primary.split(".").pop();
  const normalizedCandidate = primarySuffix ? `${customObject.key}.${primarySuffix}` : "";
  const candidates = [primary, normalizedCandidate].filter(Boolean) as string[];
  const isPrimary = candidates.map((c) => c.toLowerCase()).includes(fieldKey.toLowerCase());

  return {
    isPrimary,
    objectName: customObject.labels?.plural || "Fields",
    objectSingular: customObject.labels?.singular || "Field",
    fieldName: element.label || element.name || "Field",
  };
};

/**
 * Check if this is the only custom object field of this type
 * @param element - The element to check
 * @param elements - Form elements (for forms) or null (for surveys/quizzes)
 * @param slides - Survey/quiz slides (for surveys/quizzes) or null (for forms)
 * @returns Boolean indicating if this is the only field of this type
 */
export const isOnlyCustomObjectField = (
  element: any,
  elements: any[] | null,
  slides: any[] | null
): boolean => {
  if (!isCustomObjectField(element?.model)) {
    return false;
  }

  const objectModel = element.model;

  // Count fields with the same object model
  if (slides) {
    const fieldsWithSameModel = slides?.flatMap(
      (slide) => slide.slideData?.filter((el) => el.model === objectModel) || []
    );
    return fieldsWithSameModel?.length === 1;
  }
  const fieldsWithSameModel = elements?.filter((el) => el.model === objectModel);
  return fieldsWithSameModel?.length === 1;
};

/**
 * Handle remove all custom object fields of a specific type
 * @param elementToRemove - The element being removed
 * @param elements - Form elements array (for forms)
 * @param slides - Survey/quiz slides array (for surveys/quizzes)
 * @param store - The app store
 * @param deleteIndex - Index of element being deleted
 */
export const handleRemoveAllCustomObjectFields = (
  elementToRemove: any,
  elements: any[] | null,
  slides: any[] | null,
  store: any,
  deleteIndex: number
) => {
  if (!isCustomObjectField(elementToRemove?.model)) {
    return;
  }

  const objectModel = elementToRemove.model;

  // Remove all fields with the same object model
  if (slides) {
    slides.forEach((slide) => {
      if (slide.slideData) {
        slide.slideData = slide.slideData.filter((element) => element.model !== objectModel);
      }
    });
  } else if (elements && store) {
    // For forms, directly modify the store elements array
    store.elements = store.elements.filter((element) => element.model !== objectModel);
    // Reset sidebar if any removed element was selected
    if (store.currentCustomSidebarPreview !== undefined) {
      store.currentCustomSidebarPreview = undefined;
    }
  }

  // Clear association settings for this object model
  if (store.contactAssociationSettings?.objectModel === objectModel) {
    store.contactAssociationSettings = null;
  }
  store.currentCustomObjectModel = null;

  store.anyUnsavedChanges = true;
  store.addToHistory();

  // Clean up contact associations
  store.cleanupContactAssociations();
};

/**
 * Personal group field tags that represent contact information
 */
const PERSONAL_FIELD_TAGS = [
  "full_name",
  "first_name",
  "last_name",
  "date_of_birth",
  "phone",
  "email",
];

/**
 * Check if an element is a contact field (personal group field)
 * Contact fields are those with model === "contact" or (no model AND personal group fields)
 * @param element - The element to check
 * @returns Boolean indicating if this is a contact field
 */
export const isContactField = (element: any): boolean => {
  if (!element) {
    return false;
  }

  // Contact fields are those with:
  // 1. model === "contact" OR
  // 2. no model AND (belongs to personal group OR has personal category)
  return (
    element.model === "contact" ||
    (!element.model &&
      (element.category === "personal" || PERSONAL_FIELD_TAGS.includes(element.tag)))
  );
};

/**
 * Check if there are custom object fields in the form/survey/quiz
 * @param elements - Form elements (for forms) or null
 * @param slides - Survey/quiz slides (for surveys/quizzes) or null
 * @returns Boolean indicating if there are custom object fields
 */
export const hasCustomObjectFields = (elements: any[] | null, slides: any[] | null): boolean => {
  let allFields: any[] = [];

  if (elements) {
    // Form context
    allFields = elements;
  } else if (slides) {
    // Survey/Quiz context
    allFields = slides.flatMap((slide) => slide.slideData || []);
  }

  // Check if any field is a custom object field
  return allFields.some((field) => isCustomObjectField(field.model));
};

/**
 * Check if there are contact fields in the form/survey/quiz
 * Contact fields are those with model === "contact" or (no model AND personal group fields)
 * @param elements - Form elements (for forms) or null
 * @param slides - Survey/quiz slides (for surveys/quizzes) or null
 * @returns Boolean indicating if there are contact fields
 */
export const hasContactFields = (elements: any[] | null, slides: any[] | null): boolean => {
  let allFields: any[] = [];

  if (elements) {
    // Form context
    allFields = elements;
  } else if (slides) {
    // Survey/Quiz context
    allFields = slides.flatMap((slide) => slide.slideData || []);
  }

  // Reuse the isContactField logic for consistency
  return allFields.some((field) => isContactField(field));
};

/**
 * Interface for mixed custom object validation result
 */
export interface MixedCustomObjectValidationResult {
  isValid: boolean;
  errorInfo?: {
    existingObjectName: string;
    attemptedObjectName: string;
  };
}

/**
 * Automatically add primary field for custom object when no contact fields present
 * @param objectModel - The custom object model
 * @param store - The app store
 * @param context - Context object containing elements/slides and indices
 * @returns Boolean indicating if primary field was added
 */
export const autoAddPrimaryField = (
  objectModel: string,
  store: any,
  context: {
    elements?: any[] | null;
    slides?: any[] | null;
    slideIndex?: number;
    addedIndex: number;
  }
): boolean => {
  // Support for Business model: synthesize a pseudo "primary" field (business.name)
  if (objectModel === "business") {
    const fieldKey = "business.name";
    const primaryExists = (() => {
      if (context.elements) {
        return context.elements.some((el: any) => (el.fieldKey || "").toLowerCase() === fieldKey);
      }
      if (context.slides && context.slideIndex !== undefined) {
        return context.slides
          .flatMap((s: any) => s.slideData || [])
          .some((el: any) => (el.fieldKey || "").toLowerCase() === fieldKey);
      }
      return false;
    })();

    // For surveys/quizzes only auto-add when it's the first field of this object across all slides
    if (context.slides && context.slideIndex !== undefined) {
      const allSlideData = context.slides.flatMap((s: any) => s.slideData || []);
      const objectFieldsCount = allSlideData.filter((el: any) => el.model === objectModel).length;
      if (objectFieldsCount > 1) {
        return false;
      }
    }

    if (primaryExists) {
      return false;
    }

    // Find an existing business name field from customFields or synthesize a minimal one
    const candidate = store.customFields?.find(
      (f: any) => (f.fieldKey || "").toLowerCase() === fieldKey
    );

    const fieldToAdd = { ...candidate, active: false, edit: false, required: true };
    if (!fieldToAdd.uuid) {
      fieldToAdd.uuid = uuidv4();
    }

    if (context.elements) {
      context.elements.splice(context.addedIndex + 1, 0, fieldToAdd);
    } else if (context.slides && context.slideIndex !== undefined) {
      context.slides[context.slideIndex].slideData.splice(context.addedIndex + 1, 0, fieldToAdd);
      const slideId = context.slides[context.slideIndex]?.id;
      if (fieldToAdd.uuid) {
        if (store.quizId) {
          // Quiz builder uses id-based selection
          if (slideId) {
            store.currentActiveIndex = [slideId, fieldToAdd.uuid];
            store.currentSlideIndex = slideId;
            store.sidebarOverlaySettings = {
              elementId: fieldToAdd.uuid,
              slideId,
              formIndex: undefined,
            } as any;
          }
        } else {
          // Surveys use index-based selection
          const elementIndex = context.addedIndex + 1;
          store.currentActiveIndex = [context.slideIndex, elementIndex];
          store.currentSlideIndex = context.slideIndex;
          store.sidebarOverlaySettings = {
            index: elementIndex,
            slideIndex: context.slideIndex,
            formIndex: undefined,
          } as any;
        }
        store.isAnyElementActive = true;
      }
    }

    // When Business is added first time, auto-mark association section with business object id
    try {
      const businessObject = store.customObjects?.find((obj: any) => obj.key === "business");
      if (businessObject?.id) {
        const prev = store.contactAssociationSettings || {};
        store.contactAssociationSettings = {
          ...prev,
          objectModel: "business",

          selectedAssociation: "BUSINESSES_CONTACTS_ASSOCIATION",
          objectId: businessObject.id,
        } as any;
      }
    } catch (_) {
      // no-op: best-effort
    }

    store.anyUnsavedChanges = true;
    if (store.addToHistory) {
      store.addToHistory();
    }
    return true;
  }

  // Default flow for custom objects and Opportunity (requires primaryDisplayProperty)
  const currentObject = store.customObjects?.find((obj: any) => obj.key === objectModel);

  if (!currentObject?.primaryDisplayProperty) {
    return false;
  }

  // Check if primary field already exists
  let primaryDisplayPropertyExists = false;

  if (context.elements) {
    // Form context
    primaryDisplayPropertyExists = context.elements.some(
      (element: any) =>
        element.fieldKey === currentObject.primaryDisplayProperty ||
        element.fieldKey ===
          `${currentObject.key}.${currentObject.primaryDisplayProperty.split(".").pop()}`
    );
  } else if (context.slides && context.slideIndex !== undefined) {
    // Survey/Quiz context: check across ALL slides
    const allSlideData = context.slides.flatMap((s: any) => s.slideData || []);
    primaryDisplayPropertyExists = allSlideData.some(
      (element: any) =>
        element.fieldKey === currentObject.primaryDisplayProperty ||
        element.fieldKey ===
          `${currentObject.key}.${currentObject.primaryDisplayProperty.split(".").pop()}`
    );

    // Only auto-add when this is the first field of this object across the entire quiz
    const objectFieldsCount = allSlideData.filter((el: any) => el.model === objectModel).length;
    if (objectFieldsCount > 1) {
      return false;
    }
  }

  if (primaryDisplayPropertyExists) {
    return false;
  }

  // Find the primary field from customFields
  const primaryField = store.customFields?.find(
    (field: any) =>
      field.fieldKey === currentObject.primaryDisplayProperty ||
      field.fieldKey ===
        `${currentObject.key}.${currentObject.primaryDisplayProperty.split(".").pop()}`
  );

  if (!primaryField) {
    return false;
  }

  const fieldToAdd = {
    ...primaryField,
    active: false,
    edit: false,
    required: true,
  };

  // Assign a uuid so sidebars can target this element reliably
  if (!fieldToAdd.uuid) {
    fieldToAdd.uuid = uuidv4();
  }

  // Add the field to the appropriate location
  if (context.elements) {
    // Form context - insert right after the dropped element
    context.elements.splice(context.addedIndex + 1, 0, fieldToAdd);
  } else if (context.slides && context.slideIndex !== undefined) {
    // Survey/Quiz context - insert right after the dropped element
    context.slides[context.slideIndex].slideData.splice(context.addedIndex + 1, 0, fieldToAdd);

    // After insertion, focus the sidebar on the newly added primary field
    const slideId = context.slides[context.slideIndex]?.id;
    if (fieldToAdd.uuid) {
      if (store.quizId) {
        if (slideId) {
          store.currentActiveIndex = [slideId, fieldToAdd.uuid];
          store.currentSlideIndex = slideId;
          store.sidebarOverlaySettings = {
            elementId: fieldToAdd.uuid,
            slideId,
            formIndex: undefined,
          } as any;
        }
      } else {
        const elementIndex = context.addedIndex + 1;
        store.currentActiveIndex = [context.slideIndex, elementIndex];
        store.currentSlideIndex = context.slideIndex;
        store.sidebarOverlaySettings = {
          index: elementIndex,
          slideIndex: context.slideIndex,
          formIndex: undefined,
        } as any;
      }
      store.isAnyElementActive = true;
    }
  }

  // Update store state
  store.anyUnsavedChanges = true;
  if (store.addToHistory) {
    store.addToHistory();
  }

  return true;
};

/**
 * Handle triggering contact association modal when contact field is dropped and custom objects exist
 * @param elementDrop - The element being dropped
 * @param store - The app store
 * @param context - Context object containing elements/slides
 * @returns Boolean indicating if modal was triggered
 */
export const handleContactFieldDrop = (
  elementDrop: any,
  store: any,
  context: {
    elements?: any[] | null;
    slides?: any[] | null;
  }
): boolean => {
  // Check if this is a contact field being dropped and custom objects exist
  if (
    isContactField(elementDrop) &&
    hasCustomObjectFields(context.elements, context.slides) &&
    !store.contactAssociationSettings
  ) {
    // Find the first custom object element to use as the model
    let customObjectElement = null;

    if (context.elements) {
      // Form context
      customObjectElement = context.elements.find((element) =>
        isTrueCustomObjectField(element.model)
      );
    } else if (context.slides) {
      // Survey/Quiz context
      customObjectElement = context.slides
        .flatMap((slide) => slide.slideData || [])
        .find((element) => isTrueCustomObjectField(element.model));
    }

    if (customObjectElement) {
      store.currentCustomObjectModel = customObjectElement.model;
      store.showContactAssociationModal = true;
      return true;
    }
  }

  return false;
};

/**
 * Get display name for a model (custom object, business, or opportunity)
 * @param model - The model string
 * @param customObjects - Array of custom objects from store
 * @param t - Translation function
 * @returns Display name for the model
 */
const getModelDisplayName = (
  model: string,
  customObjects: any[],
  t: (key: string) => string
): string => {
  if (model === "business") {
    const translated = t("common.business");
    return translated && translated !== "common.business" ? translated : "Company";
  }
  if (model === "opportunity") {
    const translated = t("common.opportunity");
    return translated && translated !== "common.opportunity" ? translated : "Opportunity";
  }

  // Handle custom objects
  const customObject = customObjects?.find((obj) => obj.key === model);
  return customObject?.labels?.plural || t("common.customObjectsFallback") || "Custom Objects";
};

/**
 * Validates if adding a custom object element would create mixed custom object types
 * and handles the validation logic including removal and error display
 * @param elementDrop - The element being dropped/added
 * @param elements - Form elements array (for forms) or null
 * @param slides - Survey/quiz slides array (for surveys/quizzes) or null
 * @param slideIndex - Index of slide for surveys/quizzes (optional)
 * @param newIndex - Index where element was added
 * @param customObjects - Array of custom objects from store
 * @param t - Translation function
 * @returns Validation result with error info if validation fails
 */
export const validateMixedCustomObjectTypes = (
  elementDrop: any,
  elements: any[] | null,
  slides: any[] | null,
  slideIndex: number | null,
  newIndex: number,
  customObjects: any[],
  t: (key: string) => string
): MixedCustomObjectValidationResult => {
  // We allow only ONE associated object type (business OR opportunity OR a single true custom object)
  // alongside contact fields. Mixing multiple associated object types is not allowed.

  const normalizeObjectModel = (model?: string): string | null => {
    if (!model || model === "contact") {
      return null;
    }
    if (model === "business") {
      return "business";
    }
    if (model === "opportunity") {
      return "opportunity";
    }
    if (isTrueCustomObjectField(model)) {
      return model;
    } // keep specific custom object key
    return null;
  };

  const attemptedObject = normalizeObjectModel(elementDrop?.model);
  // If attempted is not an associated object (e.g., contact or other), it's always valid
  if (!attemptedObject) {
    return { isValid: true };
  }

  // Collect existing associated object models from context (exclude contact)
  let existingModels: string[] = [];
  if (elements) {
    existingModels = (elements || [])
      .map((el) => normalizeObjectModel(el?.model))
      .filter((m): m is string => Boolean(m));
  } else if (slides) {
    existingModels = slides
      .flatMap((slide) => slide.slideData || [])
      .map((el) => normalizeObjectModel(el?.model))
      .filter((m): m is string => Boolean(m));
  }

  const distinctExisting = Array.from(new Set(existingModels));

  // If there is an existing different associated object and the attempted one is different → invalid
  const existingDifferent = distinctExisting.find((m) => m !== attemptedObject);
  if (existingDifferent) {
    const existingObjectName = getModelDisplayName(existingDifferent, customObjects, t);
    const attemptedObjectName = getModelDisplayName(elementDrop.model, customObjects, t);
    // Remove the just-added element since we're rejecting it
    if (elements) {
      // Form context
      elements.splice(newIndex, 1);
      // Also remove any auto-added primary field for the attempted object's model
      const primaryKeySuffix = (elementDrop.fieldKey || "").split(".").pop();
      const primaryFieldIndex = elements.findIndex(
        (el: any) =>
          el.model === elementDrop.model &&
          (el.fieldKey === `${elementDrop.model}.${primaryKeySuffix}` ||
            el.fieldKey === elementDrop.fieldKey)
      );
      if (primaryFieldIndex !== -1) {
        elements.splice(primaryFieldIndex, 1);
      }
    } else if (slides && slideIndex !== null) {
      // Survey/Quiz context: prefer to remove by uuid to avoid index drift
      let removed = false;
      if (elementDrop?.uuid) {
        for (let si = 0; si < slides.length; si++) {
          const idx = (slides[si].slideData || []).findIndex(
            (el: any) => el.uuid === elementDrop.uuid
          );
          if (idx !== -1) {
            slides[si].slideData.splice(idx, 1);
            removed = true;
            break;
          }
        }
      }
      // Fallback: remove by provided index on current slide
      if (!removed && slides[slideIndex]?.slideData) {
        slides[slideIndex].slideData.splice(newIndex, 1);
      }
      // Also remove any auto-added primary field for the attempted object's model
      for (let si = 0; si < slides.length; si++) {
        const slideData = slides[si].slideData || [];
        const primaryKeySuffix = (elementDrop.fieldKey || "").split(".").pop();
        const primaryFieldIndex = slideData.findIndex(
          (el: any) =>
            el.model === elementDrop.model &&
            (el.fieldKey === `${elementDrop.model}.${primaryKeySuffix}` ||
              el.fieldKey === elementDrop.fieldKey)
        );
        if (primaryFieldIndex !== -1) {
          slideData.splice(primaryFieldIndex, 1);
        }
      }
    }

    return {
      isValid: false,
      errorInfo: { existingObjectName, attemptedObjectName },
    };
  }

  return { isValid: true };
};
